import { getEnv } from "../utils/env";
import {
  clearTokens,
  forceRefreshIdToken,
  getValidIdToken,
} from "../auth/cognito-client";
import { getStorage, triggerUnauthorized } from "../platform";

// Lazy so the env adapter has a chance to register before first request.
function getApiBaseUrl(): string {
  return getEnv("VITE_API_URL", "http://localhost:3000");
}

/** Read the active profile id (persona) from the persisted switcher store.
 * Returns null when no persona has been picked yet so the request falls back
 * to the legacy Cognito subject on the backend. */
function readActiveProfileId(): string | null {
  let storage;
  try {
    storage = getStorage();
  } catch {
    return null;
  }
  try {
    const raw = storage.getItem("slumbr-profile-switcher");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { activeProfileId?: string | null } };
    const id = parsed.state?.activeProfileId;
    return typeof id === "string" && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/**
 * Error thrown for any failed request. Carries the HTTP status so callers can
 * branch on it (403 private room, 409 save conflict, …). `status` is 0 when the
 * server was never reached. The message is unchanged from the old plain Error.
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** HTTP status of a thrown request error, or undefined for non-API errors. */
export function getApiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status;
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : undefined;
}

export interface ApiClientOptions {
  getToken: () => Promise<string | null>;
  onUnauthorized: () => void;
  /** Override the base URL (else VITE_API_URL). Used by apps on a dedicated
   *  HTTP API, e.g. Brief, which has its own gateway. Resolved lazily. */
  getBaseUrl?: () => string;
  /**
   * Recover from a 401 by minting a fresh token. Resolve with the new token to
   * have the request transparently retried (Discord-style persistent session);
   * resolve with `null` ONLY when the session is genuinely dead (refresh token
   * expired/revoked) — that's the sole trigger for `onUnauthorized`. Reject for
   * a transient failure (network/5xx): the request fails but the session is
   * preserved. Omit to fall back to logging out on any 401.
   */
  refreshToken?: () => Promise<string | null>;
}

export class ApiClient {
  private getToken: () => Promise<string | null>;
  private onUnauthorized: () => void;
  private getBaseUrl: () => string;
  private refreshToken?: () => Promise<string | null>;

  constructor(options: ApiClientOptions) {
    this.getToken = options.getToken;
    this.onUnauthorized = options.onUnauthorized;
    this.getBaseUrl = options.getBaseUrl ?? getApiBaseUrl;
    this.refreshToken = options.refreshToken;
  }

  private async request<T>(
    path: string,
    options?: RequestInit,
    isRetry = false,
    // The persona this operation was ISSUED as — captured once, so the 401-refresh retry below can never re-send
    // the same body as a different persona if the browser-wide choice changed meanwhile.
    profileId: string | null = readActiveProfileId()
  ): Promise<T> {
    const token = await this.getToken();
    const activeProfileId = profileId;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(activeProfileId ? { "X-Slumbr-Profile": activeProfileId } : {}),
      ...options?.headers,
    };

    let response: Response;
    try {
      response = await fetch(`${this.getBaseUrl()}${path}`, {
        ...options,
        headers,
      });
    } catch {
      throw new ApiError(0, "Network error: unable to reach the server");
    }

    if (response.status === 401) {
      // First 401 on a fresh attempt: try to recover the session instead of
      // nuking it. Only a fatal refresh (null) logs the user out; a transient
      // refresh failure (reject) just fails this request.
      if (!isRetry && this.refreshToken) {
        let fresh: string | null;
        try {
          fresh = await this.refreshToken();
        } catch {
          // Transient refresh failure — keep the user signed in.
          throw new ApiError(401, "Unauthorized");
        }
        if (fresh) {
          return this.request<T>(path, options, true, profileId);
        }
        // Refresh token is genuinely dead → real logout.
        this.onUnauthorized();
        throw new ApiError(401, "Unauthorized");
      }
      // Already retried with a fresh token (endpoint still 401s — not an auth
      // death), so don't log out. With no refresher configured, fall back to
      // the legacy logout behaviour.
      if (!isRetry) this.onUnauthorized();
      throw new ApiError(401, "Unauthorized");
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ error: "Request failed" }));
      throw new ApiError(
        response.status,
        (error as { error?: string }).error || `HTTP ${response.status}`
      );
    }

    // 204 No Content — nothing to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }
}

export const apiClient = new ApiClient({
  getToken: getValidIdToken,
  refreshToken: forceRefreshIdToken,
  onUnauthorized: () => {
    clearTokens();
    triggerUnauthorized();
  },
});

/** Brief runs on its own HTTP API (shared API hit the integration quota).
 *  Falls back to the main API URL if VITE_BRIEF_API_URL is unset. */
export const briefApiClient = new ApiClient({
  getToken: getValidIdToken,
  refreshToken: forceRefreshIdToken,
  onUnauthorized: () => {
    clearTokens();
    triggerUnauthorized();
  },
  getBaseUrl: () => getEnv("VITE_BRIEF_API_URL", getApiBaseUrl()),
});
